import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, MapPin } from 'lucide-react';
import { ServicePageLayout } from '../components/ServicePageLayout';
import { NotFoundPage } from './NotFoundPage';
import { getCity } from '../data/cityData';
import { BUSINESS_ID, cityPath } from '../utils/seo';
const relatedServices = [{
  label: 'Interior Detailing',
  to: '/services/interior-detailing'
}, {
  label: 'Exterior Detailing',
  to: '/services/exterior-detailing'
}, {
  label: 'Full Car Detail',
  to: '/services/full-car-detail'
}, {
  label: 'Engine Bay Detailing',
  to: '/services/engine-bay-detailing'
}, {
  label: 'Headlight Restoration',
  to: '/services/headlight-restoration'
}, {
  label: 'Mobile Detailing',
  to: '/services/mobile-detailing'
}, {
  label: 'Hybrid Ceramic Spray',
  to: '/services/hybrid-ceramic-spray'
}];
export function CityPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const city = slug ? getCity(slug) : undefined;
  // An unknown city slug used to silently redirect to the homepage, which
  // search engines read as a soft 404 (a real page answering an invalid URL).
  // Serve the noindexed 404 instead.
  if (!city) {
    return <NotFoundPage data-id="element-521" data-mp="1wyosnienanwm" />;
  }
  const baseUrl = 'https://www.fulldetailpro.com';
  const path = cityPath(city.slug);
  // Described as a Service delivered to this city rather than a LocalBusiness
  // located in it — Mike's Pro Detail is mobile and has no storefront anywhere.
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Mobile Car Detailing in ${city.name}, CA`,
    serviceType: 'Mobile Car Detailing',
    description: city.metaDescription,
    url: `${baseUrl}${path}`,
    provider: {
      '@id': BUSINESS_ID
    },
    areaServed: {
      '@type': 'City',
      name: `${city.name}, CA`
    }
  };
  const breadcrumbs = [{
    name: 'Home',
    item: `${baseUrl}/`
  }, {
    name: 'Service Areas',
    item: `${baseUrl}/#service-areas`
  }, {
    name: city.name,
    item: `${baseUrl}${path}`
  }];
  return <ServicePageLayout title={city.title} subtitle={city.subtitle} metaDescription={city.metaDescription} schema={schema} faqs={city.faqs} breadcrumbs={breadcrumbs} data-id="element-522" data-mp="1stxzalxfcm3w">
      {city.paragraphs.map((p, i) => <p key={i} data-id="element-523" data-mp="22nwwd6in5foz" data-mpi={i}>{p}</p>)}

      <h2 data-id="element-524" data-mp="1xmhcviw43h8p">Mobile Detailing Services in {city.name}</h2>
      <p data-id="element-525" data-mp="1xnso23rvzr9u">
        We bring the full range of professional auto detailing to {city.name},
        with every service performed on-site at your home or office. Popular
        options for {city.name} drivers include:
      </p>
      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-526" data-mp="1iy4qndmttcre">
        <li className="flex items-start" data-id="element-527" data-mp="1izo6j34kllsn">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-528" data-mp="1ewj6d77ypw76" />
          <span data-id="element-529" data-mp="1ev8276alumfj">
            <strong data-id="element-530" data-mp="1eyvuk24bd2so">Interior Detailing:</strong> Deep vacuuming, shampooing,
            steam sanitizing, and leather conditioning for a like-new cabin.
          </span>
        </li>
        <li className="flex items-start" data-id="element-531" data-mp="2qbevxo85di2i">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-532" data-mp="2xuo45e4x3apz" />
          <span data-id="element-533" data-mp="2xxk2h3kog1ey">
            <strong data-id="element-534" data-mp="32u0q7wnysdri">Exterior Detailing:</strong> Safe hand wash, clay bar
            decontamination, and durable wax or sealant protection.
          </span>
        </li>
        <li className="flex items-start" data-id="element-535" data-mp="2nz4akydtso4e">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-536" data-mp="2svkxxqd7aygf" />
          <span data-id="element-537" data-mp="2sxxlqk5n431e">
            <strong data-id="element-538" data-mp="3glytvrywphga">Full Car Detail:</strong> Our most popular bumper-to-bumper
            package combining interior and exterior services.
          </span>
        </li>
        <li className="flex items-start" data-id="element-539" data-mp="3lmurxowku9ds">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-540" data-mp="36wnlk7juggtg" />
          <span data-id="element-541" data-mp="3buyw6khyqvcq">
            <strong data-id="element-542" data-mp="3bugpsv1l70r3">Engine Bay & Headlight Restoration:</strong> Specialty
            services to keep every part of your vehicle looking its best.
          </span>
        </li>
      </ul>

      <h2 data-id="element-543" data-mp="85lrxmfpuzgg">Neighborhoods We Serve in {city.name}</h2>
      <p data-id="element-544" data-mp="87get6ajt1n6">
        Our mobile detailing service reaches every corner of {city.name},
        including {city.neighborhoods.slice(0, -1).join(', ')}, and{' '}
        {city.neighborhoods[city.neighborhoods.length - 1]}. Wherever you park,
        we come to you with our own water and detailing equipment. We require
        access to a standard electrical outlet at the service location.
      </p>
      <div className="not-prose flex flex-wrap gap-3 my-6" data-id="element-545" data-mp="21r8ju5suj3rt">
        {city.neighborhoods.map(n => <span key={n} className="inline-flex items-center gap-2 bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-gray-300" data-id="element-546" data-mp="2zc4knfvpfod2">
            <MapPin className="w-4 h-4 text-red-500" data-id="element-547" data-mp="2pnmoxd2a87ga" />
            {n}
          </span>)}
      </div>

      <h2 data-id="element-548" data-mp="2kojj3qeh9x79">Explore Our Detailing Services</h2>
      <p data-id="element-549" data-mp="2kni3d8miomj7">
        Learn more about each of our professional mobile detailing services
        available in {city.name} and throughout Ventura County, or head back to
        our <Link to="/" data-id="element-550" data-mp="3dcp3ud5j7m73">Ventura County mobile detailing homepage</Link> for
        full pricing and packages:
      </p>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-551" data-mp="3lmmnjtb8hskj">
        {relatedServices.map(s => <Link key={s.to} to={s.to} className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-552" data-mp="3pohqt3cq6g0y">
            {s.label} &rarr;
          </Link>)}
      </div>
    </ServicePageLayout>;
}